import { Box, Typography, TextField } from '@mui/material';
import { COLORS } from '../utils/constants';
import { useTranslation } from '@/kit/useTranslation';

/**
 * Modern dimensions input (Length × Breadth × Height)
 * Supports compact mode for inline display
 */
export default function DimensionsInput({ volume, onChange, compact = false }) {
  const { t } = useTranslation();
  const handleChange = (field, value) => {
    const numValue = value === '' ? '' : Number(value);
    if (numValue === '' || numValue >= 0) {
      onChange({ ...volume, [field]: numValue });
    }
  };

  const calculatedVolume =
    volume.length && volume.breadth && volume.height
      ? (parseFloat(volume.length) * parseFloat(volume.breadth) * parseFloat(volume.height)) /
        1000000
      : null;

  const inputSx = {
    flex: 1,
    minWidth: compact ? 50 : 60,
    '& .MuiOutlinedInput-root': {
      borderRadius: compact ? 1.5 : 2,
      backgroundColor: COLORS.white,
      '& fieldset': {
        borderColor: COLORS.border,
        borderWidth: 1,
      },
      '&:hover fieldset': { borderColor: COLORS.primary },
      '&.Mui-focused fieldset': {
        borderColor: COLORS.primary,
        borderWidth: 2,
      },
    },
    '& input': {
      fontWeight: 600,
      color: COLORS.textPrimary,
      textAlign: 'center',
      padding: compact ? '6px 4px' : '8px 6px',
      fontSize: compact ? '0.8rem' : '0.875rem',
    },
  };

  return (
    <Box>
      {!compact && (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: COLORS.textPrimary,
            mb: 1.5,
            fontSize: '0.875rem',
            letterSpacing: '0.5px',
          }}
        >
          {t('estimate.dimensions.title')}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: compact ? 0.5 : 1, alignItems: 'center' }}>
        <TextField
          size="small"
          type="number"
          placeholder={t('estimate.dimensions.placeholderL')}
          value={volume.length}
          onChange={(e) => handleChange('length', e.target.value)}
          inputProps={{ min: 0, step: 0.1 }}
          sx={inputSx}
        />
        <Typography
          sx={{
            fontWeight: 700,
            color: COLORS.textTertiary,
            fontSize: compact ? '0.8rem' : '0.95rem',
          }}
        >
          ×
        </Typography>
        <TextField
          size="small"
          type="number"
          placeholder={t('estimate.dimensions.placeholderB')}
          value={volume.breadth}
          onChange={(e) => handleChange('breadth', e.target.value)}
          inputProps={{ min: 0, step: 0.1 }}
          sx={inputSx}
        />
        <Typography
          sx={{
            fontWeight: 700,
            color: COLORS.textTertiary,
            fontSize: compact ? '0.8rem' : '0.95rem',
          }}
        >
          ×
        </Typography>
        <TextField
          size="small"
          type="number"
          placeholder={t('estimate.dimensions.placeholderH')}
          value={volume.height}
          onChange={(e) => handleChange('height', e.target.value)}
          inputProps={{ min: 0, step: 0.1 }}
          sx={inputSx}
        />
        {compact && (
          <Typography
            sx={{
              color: COLORS.textSecondary,
              fontSize: '0.7rem',
              fontWeight: 600,
              ml: 0.5,
            }}
          >
            {t('estimate.dimensions.unit')}
          </Typography>
        )}
      </Box>
      {!compact && calculatedVolume && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 1,
            color: COLORS.textSecondary,
            fontWeight: 600,
            fontSize: '0.75rem',
          }}
        >
          {t('estimate.dimensions.volume', { value: calculatedVolume.toFixed(4) })}
        </Typography>
      )}
    </Box>
  );
}
